import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";
import { useSettings } from "@/hooks/SettingsContext";

const VISION_ITEMS = [
  {
    title: "Evangelism",
    text: "The Gospel taken outside the four walls is the absolute foundation of our church. We emphasize personal evangelism, and a large variety of outreaches to share the Gospel.",
    verse: "And He said to them, 'Go into all the world and preach the gospel to every creature.'",
    ref: "Mark 16:15",
  },
  {
    title: "Conversion",
    text: "We witness, testify and preach for a decision. The Gospel has the power to save and transform lives. The bulk of our congregation consists of people converted here by the power of God.",
    verse: "Having been born again, not of corruptible seed but incorruptible, through the word of God which lives and abides forever.",
    ref: "1 Peter 1:23",
  },
  {
    title: "The Local Church",
    text: "God has placed everything necessary to accomplish His will in the setting of the local church. God's will is accomplished in us and through us as we connect and commit ourselves to a local church.",
    verse: "...the church, which is His body, the fullness of Him who fills everything in every way.",
    ref: "Ephesians 1:22-23",
  },
  {
    title: "Discipleship",
    text: "God intends for calling to Him, and for Him, to be accomplished through being trained and equipped within the setting of a local church. Therefore, the highest calling of a pastor and congregation is to obey God's command to make disciples.",
    verse: "Go therefore and make disciples of all the nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit, teaching them to observe all things that I have commanded you; and lo, I am with you always, even to the end of the age.",
    ref: "Matthew 28:19-20",
  },
  {
    title: "Church Planting",
    text: "The result of disciples being raised up is that they be released into the harvest field. We send couples to plant brand new congregations, with the aim of reproducing our vision in a new area.",
    verse: "Then He said to His disciples, 'The harvest truly is plentiful, but the laborers are few. Therefore pray the Lord of the harvest to send out laborers into His harvest.'",
    ref: "Matthew 9:37-38",
  },
  {
    title: "World Evangelism",
    text: "From the earliest Bible record, God's vision is worldwide testimony and impact. The Gospel message and the vision God has revealed to us is not simply American, but God-inspired - so it works, and is relevant, in every nation and culture of the world. Our job as believers, and as the local church, is to pray, train, invest, send and support workers to go into all the world.",
    verse: "Go into all the world... every person. Make disciples of all the nations.",
    ref: "Mark 16:15 - Matthew 28:19",
  },
];

export default function Vision() {
  const { settings } = useSettings();
  const s = settings || {};
  return (
    <>
      <SEO
        title="Our Vision - Evangelism, Discipleship & Church Planting"
        description="Victory Chapel is a local church in Clay County, FL with a worldwide vision: evangelism, conversion, the local church, discipleship, church planting and world evangelism - fulfilling the Great Commission."
        path="/vision"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Our Vision</p>
          <LineReveal
            className="mt-4 font-display text-5xl font-900 leading-[1.0] tracking-tighter sm:text-6xl lg:text-7xl"
            lines={["A LOCAL CHURCH WITH", "A WORLDWIDE VISION."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg" data-testid="vision-page-intro">
              {s.vision_text || "We're a local church with a worldwide vision. Our goal is to fulfill the Great Commission by making disciples and planting churches."}
              {" "}Here is the vision that drives everything we do.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-24" data-testid="vision-pillars-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          {VISION_ITEMS.map((item, i) => (
            <Reveal key={item.title} delay={0.05}>
              <article
                className={`grid gap-5 py-10 sm:grid-cols-12 sm:gap-8 sm:py-12 ${i > 0 ? "border-t border-navy/10" : ""}`}
                data-testid={`vision-item-${i + 1}`}
              >
                <div className="sm:col-span-4">
                  <p className="font-display text-5xl font-900 leading-none text-navy/10" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-3 font-display text-2xl font-800 tracking-tight text-navy sm:text-3xl">
                    {item.title}
                  </h2>
                </div>
                <div className="sm:col-span-8">
                  <p className="text-base leading-relaxed text-slate-600 sm:text-lg">{item.text}</p>
                  <blockquote className="mt-5 border-l-2 border-victory pl-5">
                    <p className="font-serif text-base italic leading-relaxed text-navy/90 sm:text-lg">
                      "{item.verse}"
                    </p>
                    <cite className="mt-2 block font-display text-xs font-700 uppercase not-italic tracking-[0.25em] text-victory">
                      {item.ref}
                    </cite>
                  </blockquote>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
